import React, { useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";

import { faUsers } from "@fortawesome/pro-solid-svg-icons/faUsers";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cn from "classnames";
import { defineMessages, useIntl } from "react-intl";
import { useHistory } from "react-router-dom";

import AnyColor from "@mapmycustomers/shared/types/AnyColor";
import Team from "@mapmycustomers/shared/types/Team";
import User from "@mapmycustomers/shared/types/User";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import getInitials from "@mapmycustomers/shared/util/ui/getInitials";
import { EditableText } from "@mapmycustomers/ui";

import ChangePasswordModal from "@app/component/auth/ChangePasswordModal";
import Chip from "@app/component/Chip";
import ContactInfo from "@app/component/ContactInfo/ContactInfo";
import LastActivityChip from "@app/component/LastActivityChip";
import Path from "@app/enum/Path";
import SettingPath from "@app/enum/settings/SettingPath";
import ViewType from "@app/scene/dashboard/enum/ViewType";
import { addCard } from "@app/scene/dashboard/store/actions";
import { getBoards } from "@app/scene/dashboard/store/selectors";
import canEditBoard from "@app/scene/dashboard/utils/canEditBoard";
import isUserLocationLayerAvailable from "@app/scene/map/utils/isUserLocationLayerAvailable";
import { getMe, getOrganizationSettings } from "@app/store/iam";
import {
  deleteProfilePhoto,
  showChangePasswordModal,
  updateMe,
  uploadProfilePhoto,
} from "@app/store/iam/actions";
import { isManager, isMember, isOwner, isReadOnly } from "@app/store/iam/util";
import { getUsersOfEntireOrganization } from "@app/store/members";
import { updateUser } from "@app/store/members/actions";
import { RootState } from "@app/store/rootReducer";
import Dashboard from "@app/types/dashboard/Dashboard";
import Scope from "@app/types/dashboard/Scope";
import Iam from "@app/types/Iam";
import useAnalytics from "@app/util/contexts/useAnalytics";
import { complementFileUrl } from "@app/util/file";
import { userDisplayName } from "@app/util/formatters";

import AddCardWizard from "../../addEditCard/AddCardWizard";
import useAddCard from "../../DashboardContent/util/useAddCard";

import BaseHeader from "./BaseHeader";
import EllipsisMenu from "./EllipsisMenu";
import styles from "./HeaderCommon.module.scss";
import Image from "./Image";

const messages = defineMessages({
  addChart: {
    id: "dashboard.header.member.menu.addChart",
    defaultMessage: "Add Chart",
    description: "Add chart menu item in Dashboard's header for the member scope",
  },
  addProfileImage: {
    id: "dashboard.header.member.image.noImagePlaceholder.editable",
    defaultMessage: "Add Profile image",
    description:
      "A text to show when there's no user avatar in Dashboard's header for the member scope, but current user can add one",
  },
  changePassword: {
    id: "dashboard.header.member.menu.changePassword",
    defaultMessage: "Change Password",
    description: "Change Password menu item in Dashboard's header for the member scope",
  },
  editDetails: {
    id: "dashboard.header.member.menu.editDetails",
    defaultMessage: "Edit User Details",
    description: "Edit Details menu item in Dashboard's header for the member scope",
  },
  noProfileImage: {
    id: "dashboard.header.member.image.noImagePlaceholder",
    defaultMessage: "No Profile image",
    description:
      "A text to show when there's no user avatar in Dashboard's header for the member scope",
  },
});

interface MemberHeaderContentProps {
  auto?: boolean;
  colors?: AnyColor[];
  editable?: boolean;
  onGradientChange?: (
    settings: Partial<Pick<Dashboard["settings"], "gradient" | "gradientFromImage">>
  ) => void;
  userId: User["id"];
  view: ViewType;
}

interface Props extends MemberHeaderContentProps {
  boards: Dashboard[];
  me: Iam;
  onAddCard: typeof addCard.request;
  onChangeProfilePhoto: typeof uploadProfilePhoto.request;
  onDeleteProfilePhoto: typeof deleteProfilePhoto.request;
  onShowChangePasswordModal: () => void;
  onUpdateMe: typeof updateMe.request;
  onUpdateUser: typeof updateUser.request;
  scope: Scope;
  userLocationEnabled?: boolean;
  users: User[];
}

const MemberHeaderContent: React.FC<Props> = ({
  auto,
  boards,
  colors,
  editable,
  me,
  onAddCard,
  onChangeProfilePhoto,
  onDeleteProfilePhoto,
  onGradientChange,
  onShowChangePasswordModal,
  onUpdateMe,
  onUpdateUser,
  scope,
  userId,
  userLocationEnabled,
  users,
  view,
}) => {
  const analytics = useAnalytics();
  const intl = useIntl();
  const history = useHistory();
  const user = users.find(({ id }) => id === userId);
  const isCurrentUser = me.id === userId;
  const [editing, setEditing] = useState(false);
  const [addCardWizardVisible, showAddCardWizard, hideAddCardWizard] = useBoolean();

  useEffect(() => setEditing(false), [userId]);

  const handleChangeUserDetailsClick = useCallback(() => {
    history.push(`${Path.SETTINGS}/${SettingPath.PERSONAL_PROFILE}`);
  }, [history]);

  const handleOpenTeam = useCallback(
    (id: Team["id"]) => history.push(`${Path.DASHBOARD}/teams/${id}`),
    [history]
  );

  const handleSaveName = useCallback(
    (name: string) => {
      if (!name.trim()) {
        return;
      }
      if (isCurrentUser) {
        onUpdateMe({ me: { ...me, fullName: name.trim() } });
      } else if (user) {
        onUpdateUser({ ...user, fullName: name.trim() });
      }
    },
    [isCurrentUser, me, onUpdateMe, onUpdateUser, user]
  );

  const handleChangeProfilePhoto = useCallback(
    (file: File) => {
      analytics.clicked(["Change profile picture"]);
      onChangeProfilePhoto?.(file);
    },
    [onChangeProfilePhoto, analytics]
  );

  const handleRemoveProfilePhoto = useCallback(() => {
    analytics.clicked(["Change profile picture"]);
    onDeleteProfilePhoto?.();
  }, [onDeleteProfilePhoto, analytics]);

  const canAddCharts = canEditBoard(me, scope, "add");
  const handleAddChart = useAddCard(boards, me, scope, onAddCard);

  if (!user) {
    return null;
  }

  const logoUrl = complementFileUrl(user.profilePhoto?.publicURI);

  const isNameEditAllowed =
    isOwner(me) || (isManager(me) && (isMember(user) || isReadOnly(user))) || isCurrentUser;

  const initials = user ? getInitials(user) : undefined;

  return (
    <BaseHeader
      auto={auto}
      colors={colors}
      editable={editable}
      logoUrl={logoUrl}
      onGradientChange={onGradientChange}
    >
      <Image
        alt="user logo"
        className={cn(styles.memberImage, { [styles.initials]: !isCurrentUser && !logoUrl })}
        onChange={isCurrentUser ? handleChangeProfilePhoto : undefined}
        onRemove={isCurrentUser ? handleRemoveProfilePhoto : undefined}
        src={logoUrl}
      >
        {isCurrentUser ? intl.formatMessage(messages.addProfileImage) : initials}
      </Image>
      <div className={styles.info}>
        <div className={styles.title}>
          <EditableText
            className={cn(styles.name, { [styles.nameEditing]: editing })}
            editable={isNameEditAllowed}
            editing={editing}
            onChange={handleSaveName}
            onEditingChange={setEditing}
            required
            value={editing ? user.fullName ?? "" : userDisplayName(user)}
          />
          {!editing && <ContactInfo user={user} />}
        </div>
        <div className={styles.chips}>
          <LastActivityChip
            clickable={userLocationEnabled}
            lastActivity={user.lastActivity}
            userId={user.id}
          />
          {user.teams.map((team) => (
            <Chip
              className={cn(styles.chip, { [styles.chipHover]: !isMember(me) && !isReadOnly(me) })}
              icon={<FontAwesomeIcon icon={faUsers} />}
              key={team.id}
              onClick={!isMember(me) && !isReadOnly(me) ? () => handleOpenTeam(team.id) : undefined}
            >
              {team.name}
            </Chip>
          ))}
        </div>
      </div>
      {addCardWizardVisible && (
        <AddCardWizard onAddCard={handleAddChart} onHide={hideAddCardWizard} scope={scope} />
      )}
      {isCurrentUser && (
        <EllipsisMenu
          items={[
            {
              key: "details",
              label: intl.formatMessage(messages.editDetails),
              onClick: handleChangeUserDetailsClick,
            },
            {
              key: "password",
              label: intl.formatMessage(messages.changePassword),
              onClick: onShowChangePasswordModal,
            },
            ...(canAddCharts && view === ViewType.DASHBOARD
              ? [
                  {
                    key: "addChart",
                    label: intl.formatMessage(messages.addChart),
                    onClick: showAddCardWizard,
                  },
                ]
              : []),
          ]}
        />
      )}
      <ChangePasswordModal />
    </BaseHeader>
  );
};

const mapStateToProps = (state: RootState) => {
  const me = getMe(state)!;
  const orgSettings = getOrganizationSettings(state);
  return {
    boards: getBoards(state),
    me,
    userLocationEnabled: isUserLocationLayerAvailable(me, orgSettings),
    users: getUsersOfEntireOrganization(state),
  };
};

const mapDispatchToProps = {
  onAddCard: addCard.request,
  onChangeProfilePhoto: uploadProfilePhoto.request,
  onDeleteProfilePhoto: deleteProfilePhoto.request,
  onShowChangePasswordModal: showChangePasswordModal,
  onUpdateMe: updateMe.request,
  onUpdateUser: updateUser.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(MemberHeaderContent);
